import _ from "lodash";
import client from "lib/apolloClient/client";
import { cache } from "lib/apolloClient";
import { getOrganizationGlobalConstantsFromCache } from "modules/CommonGraphqlHelpers";
import {
  getBehaviourFeedCategoriesQuery,
  getActionsQuery,
  getSentimentsQuery,
  getCurriculumProgramsQuery,
  getBehaviourSeveritiesQuery,
  getNomenclatureQuery,
  getMultiLevelDraftBehaviourCategoryQuery,
  getBehaviourSentimentWiseSeveritiesQuery,
} from "./BehaviourConfigurationQueries";
import { getBehaviourLocationsQuery } from "BehaviourManagement/modules/BehaviourManagementQueries";
import { getBehaviourNomenclatureFiltersMemoized } from "./Utils";
import { behaviourCategoryFragment } from "./BehaviourConfigurationFragments";

export const getBehaviourFeedCategoriesFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getBehaviourFeedCategoriesQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getActionsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getActionsQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const writeActionsToCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getActionsQuery,
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};

export const getSentimentsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getSentimentsQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getCurriculumProgramsFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getCurriculumProgramsQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const writeBehaviourLocationsToCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getBehaviourLocationsQuery,
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};

export const writeBehaviourFeedCategoriesToCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getBehaviourFeedCategoriesQuery,
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};

export const getBehaviourSeveritiesFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getBehaviourSeveritiesQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getBehaviourSentimentWiseSeveritiesFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getBehaviourSentimentWiseSeveritiesQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const writeBehaviourSentimentWiseSeveritiesToCache = ({
  variables,
  data,
}) => {
  try {
    client.writeQuery({
      query: getBehaviourSentimentWiseSeveritiesQuery,
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};

export const getNomenclatureFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getNomenclatureQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getBehaviourNomenclatureFromCache = ({ organizationId }) => {
  const queryVariables = getBehaviourNomenclatureFiltersMemoized({
    organizationId,
  });

  const queryData = getOrganizationGlobalConstantsFromCache(queryVariables);

  const globalConstants = _.get(queryData, "node.globalConstants", []);

  const NOMENCLATURE_NAME_MAP = {
    BEHAVIOUR_MODULE_NOMENCLATURE_ACTION: "ACTION",
    BEHAVIOUR_MODULE_NOMENCLATURE_LEVEL: "LEVEL",
    BEHAVIOUR_MODULE_NOMENCLATURE_INCIDENT: "INCIDENT",
    BEHAVIOUR_MODULE_NOMENCLATURE_MODULE: "MODULE",
  };

  const NOMENCLATURE_FALLBACK_MAP = {
    [NOMENCLATURE_NAME_MAP.BEHAVIOUR_MODULE_NOMENCLATURE_MODULE]:
      "Behaviour management",
    [NOMENCLATURE_NAME_MAP.BEHAVIOUR_MODULE_NOMENCLATURE_INCIDENT]: "Incident",
    [NOMENCLATURE_NAME_MAP.BEHAVIOUR_MODULE_NOMENCLATURE_ACTION]: "Action",
    [NOMENCLATURE_NAME_MAP.BEHAVIOUR_MODULE_NOMENCLATURE_LEVEL]: "Level",
  };

  const nomenclatureConstants = _.reduce(
    globalConstants,
    (acc, item) => {
      const { name, value } = item;
      const customName = NOMENCLATURE_NAME_MAP[name];

      if (!customName) {
        return acc;
      }

      return {
        ...acc,
        [customName]: value,
      };
    },
    {}
  );

  _.forEach(NOMENCLATURE_FALLBACK_MAP, (fallbackValue, nomenclatureKey) => {
    if (_.isEmpty(nomenclatureConstants[nomenclatureKey])) {
      nomenclatureConstants[nomenclatureKey] = fallbackValue;
    }
  });

  return nomenclatureConstants;
};

export const writeNomenclatureToCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getNomenclatureQuery,
      variables,
      data,
    });
  } catch (e) {
    return {};
  }
};

export const getMultiLevelDraftBehaviourCategoriesFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getMultiLevelDraftBehaviourCategoryQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const updateBehaviourCategoryFragment = ({ data, id }) => {
  try {
    cache.updateFragment(
      {
        id: `BehaviourCategory:${id}`,
        fragment: behaviourCategoryFragment,
        fragmentName: "behaviourCategoryFragment",
      },
      prevData => ({
        ...prevData,
        ...data,
      })
    );
  } catch (e) {
    return {};
  }
};
